import { supabase } from 'src/lib/supabaseClient';
import { emailService } from './emailService';
import { format, addMinutes, subMinutes, isAfter, isBefore } from 'date-fns';

export interface SessionReminderData {
  id: string;
  sessionId: string;
  studentEmail: string;
  tutorEmail: string;
  studentName: string;
  tutorName: string;
  sessionDate: string;
  sessionTime: string;
  subject: string;
  meetingUrl: string;
  sessionType: string;
  timezone: string;
  reminderType: 'email' | 'sms' | 'push';
  reminderTime: string; // When to send the reminder
  status: 'pending' | 'sent' | 'failed';
  scheduledAt: string;
  sentAt?: string;
  errorMessage?: string;
}

export interface SessionForReminder {
  id: string;
  scheduled_at: string;
  duration_min: number;
  status: string;
  student_id: string;
  tutor_id: string;
  topic: {
    name: string;
  } | null;
  student_profile: {
    first_name: string;
    last_name: string;
    email: string;
    timezone?: string;
  } | null;
  tutor_profile: {
    first_name: string;
    last_name: string;
    email: string;
    timezone?: string;
  } | null;
  meeting_session?: {
    meeting_url: string;
  } | null;
  session_type: string;
}

// Raw type from Supabase query
interface SessionQueryResult {
  id: string;
  scheduled_at: string;
  duration_min: number;
  status: string;
  student_id: string;
  tutor_id: string;
  topic: { name: string }[] | null;
  student_profile: {
    first_name: string;
    last_name: string;
    email: string;
    timezone?: string;
  }[] | null;
  tutor_profile: {
    first_name: string;
    last_name: string;
    email: string;
    timezone?: string;
  }[] | null;
  meeting_session: {
    meeting_url: string;
  }[] | null;
  session_type: string;
}

export interface ReminderSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  reminderTimes: number[]; // Minutes before session (e.g., [15, 60, 1440])
}

class SessionReminderService {
  private readonly defaultReminderTimes = [15, 60]; // 15 minutes and 1 hour before
  private readonly defaultSettings: ReminderSettings = {
    emailEnabled: true,
    smsEnabled: false,
    pushEnabled: false,
    reminderTimes: this.defaultReminderTimes
  };

  /**
   * Schedule reminders for a session
   */
  async scheduleSessionReminders(sessionId: string): Promise<boolean> {
    try {
      console.log(`📅 Scheduling reminders for session ${sessionId}`);

      // Get session details
      const session = await this.getSessionForReminder(sessionId);
      if (!session) {
        console.error(`❌ Session ${sessionId} not found`);
        return false;
      }

      // Get user reminder preferences (or use defaults)
      const settings = await this.getUserReminderSettings(session.student_id);

      // Schedule reminders for each configured time
      const reminderPromises = settings.reminderTimes.map(async (minutesBefore) => {
        const reminderTime = subMinutes(new Date(session.scheduled_at), minutesBefore);
        
        // Only schedule if reminder time is in the future
        if (isAfter(reminderTime, new Date())) {
          return this.createReminderRecord(session, reminderTime, minutesBefore);
        }
        return null;
      });

      const results = await Promise.all(reminderPromises);
      const successCount = results.filter(r => r !== null).length;

      console.log(`✅ Scheduled ${successCount} reminders for session ${sessionId}`);
      return successCount > 0;

    } catch (error) {
      console.error(`❌ Error scheduling reminders for session ${sessionId}:`, error);
      return false;
    }
  }

  /**
   * Process pending reminders (called by cron job or scheduler)
   */
  async processPendingReminders(): Promise<void> {
    try {
      console.log('🔄 Processing pending session reminders...');

      // Get reminders that should be sent now (within the next 5 minutes)
      const now = new Date();
      const fiveMinutesFromNow = addMinutes(now, 5);

      const { data: pendingReminders, error } = await supabase
        .from('session_reminders')
        .select('*')
        .eq('status', 'pending')
        .gte('reminder_time', now.toISOString())
        .lte('reminder_time', fiveMinutesFromNow.toISOString())
        .order('reminder_time', { ascending: true });

      if (error) {
        console.error('❌ Error fetching pending reminders:', error);
        return;
      }

      if (!pendingReminders || pendingReminders.length === 0) {
        console.log('✅ No pending reminders to process');
        return;
      }

      console.log(`📧 Found ${pendingReminders.length} reminders to process`);

      // Process each reminder
      for (const reminder of pendingReminders) {
        await this.sendReminder(reminder);
        // Add small delay to avoid overwhelming the email service
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.log('✅ Finished processing pending reminders');

    } catch (error) {
      console.error('❌ Error processing pending reminders:', error);
    }
  }

  /**
   * Send a specific reminder
   */
  private async sendReminder(reminderData: any): Promise<void> {
    try {
      console.log(`📧 Sending reminder for session ${reminderData.session_id}`);

      // Get fresh session data
      const session = await this.getSessionForReminder(reminderData.session_id);
      if (!session) {
        await this.markReminderAsFailed(reminderData.id, 'Session not found');
        return;
      }

      // Check if session is still valid (not cancelled, etc.)
      if (session.status !== 'scheduled') {
        console.log(`⏭️ Skipping reminder for session ${session.id} - status: ${session.status}`);
        await this.markReminderAsFailed(reminderData.id, `Session status: ${session.status}`);
        return;
      }

      // Validate required data before proceeding
      if (!session.student_profile || !session.tutor_profile || !session.topic) {
        await this.markReminderAsFailed(reminderData.id, 'Missing required session data');
        return;
      }

      // Calculate minutes until session
      const sessionTime = new Date(session.scheduled_at);
      const now = new Date();
      const minutesUntilSession = Math.round((sessionTime.getTime() - now.getTime()) / (1000 * 60));

      // Prepare reminder data
      const emailData = {
        studentName: `${session.student_profile.first_name} ${session.student_profile.last_name}`,
        tutorName: `${session.tutor_profile.first_name} ${session.tutor_profile.last_name}`,
        tutorEmail: session.tutor_profile.email,
        sessionDate: format(sessionTime, 'EEEE, MMMM d, yyyy'),
        sessionTime: format(sessionTime, 'h:mm a'),
        subject: session.topic.name,
        meetingUrl: session.meeting_session?.meeting_url || '',
        sessionType: session.session_type,
        minutesUntilSession: Math.max(minutesUntilSession, 0),
        timezone: session.student_profile.timezone || 'UTC'
      };

      // Send email reminder
      const emailResult = await emailService.sendSessionReminder(
        session.student_profile.email,
        emailData
      );

      if (emailResult.success) {
        await this.markReminderAsSent(reminderData.id);
        console.log(`✅ Reminder sent successfully for session ${session.id}`);
      } else {
        await this.markReminderAsFailed(reminderData.id, emailResult.error || 'Email send failed');
        console.error(`❌ Failed to send reminder for session ${session.id}:`, emailResult.error);
      }

    } catch (error) {
      console.error(`❌ Error sending reminder:`, error);
      await this.markReminderAsFailed(reminderData.id, error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Get session data for reminder
   */
  private async getSessionForReminder(sessionId: string): Promise<SessionForReminder | null> {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id,
          scheduled_at,
          duration_min,
          status,
          student_id,
          tutor_id,
          session_type,
          topic:topics(
            name
          ),
          student_profile:profiles!sessions_student_id_fkey(
            first_name,
            last_name,
            email,
            timezone
          ),
          tutor_profile:profiles!sessions_tutor_id_fkey(
            first_name,
            last_name,
            email,
            timezone
          ),
          meeting_session:meeting_sessions(
            meeting_url
          )
        `)
        .eq('id', sessionId)
        .single();

      if (error) {
        console.error('Error fetching session for reminder:', error);
        return null;
      }

      // Transform the raw query result to our expected format
      const rawData = data as SessionQueryResult;
      
      const transformedData: SessionForReminder = {
        id: rawData.id,
        scheduled_at: rawData.scheduled_at,
        duration_min: rawData.duration_min,
        status: rawData.status,
        student_id: rawData.student_id,
        tutor_id: rawData.tutor_id,
        session_type: rawData.session_type,
        topic: rawData.topic && rawData.topic.length > 0 ? rawData.topic[0] : null,
        student_profile: rawData.student_profile && rawData.student_profile.length > 0 ? rawData.student_profile[0] : null,
        tutor_profile: rawData.tutor_profile && rawData.tutor_profile.length > 0 ? rawData.tutor_profile[0] : null,
        meeting_session: rawData.meeting_session && rawData.meeting_session.length > 0 ? rawData.meeting_session[0] : null,
      };

      // Validate required data
      if (!transformedData.topic || !transformedData.student_profile || !transformedData.tutor_profile) {
        console.error('Missing required session data for reminder:', {
          topic: !!transformedData.topic,
          student_profile: !!transformedData.student_profile,
          tutor_profile: !!transformedData.tutor_profile
        });
        return null;
      }

      return transformedData;

    } catch (error) {
      console.error('Error in getSessionForReminder:', error);
      return null;
    }
  }

  /**
   * Create a reminder record in the database
   */
  private async createReminderRecord(
    session: SessionForReminder,
    reminderTime: Date,
    minutesBefore: number
  ): Promise<boolean> {
    try {
      // Validate required data
      if (!session.student_profile || !session.tutor_profile || !session.topic) {
        console.error('Cannot create reminder record: missing required session data');
        return false;
      }

      const { error } = await supabase
        .from('session_reminders')
        .insert({
          session_id: session.id,
          student_email: session.student_profile.email,
          tutor_email: session.tutor_profile.email,
          student_name: `${session.student_profile.first_name} ${session.student_profile.last_name}`,
          tutor_name: `${session.tutor_profile.first_name} ${session.tutor_profile.last_name}`,
          session_date: format(new Date(session.scheduled_at), 'yyyy-MM-dd'),
          session_time: format(new Date(session.scheduled_at), 'HH:mm:ss'),
          subject: session.topic.name,
          meeting_url: session.meeting_session?.meeting_url || '',
          session_type: session.session_type,
          timezone: session.student_profile.timezone || 'UTC',
          reminder_type: 'email',
          reminder_time: reminderTime.toISOString(),
          status: 'pending',
          minutes_before: minutesBefore,
          scheduled_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error creating reminder record:', error);
        return false;
      }

      console.log(`📝 Created reminder record for session ${session.id} - ${minutesBefore} minutes before`);
      return true;

    } catch (error) {
      console.error('Error in createReminderRecord:', error);
      return false;
    }
  }

  /**
   * Mark reminder as sent
   */
  private async markReminderAsSent(reminderId: string): Promise<void> {
    await supabase
      .from('session_reminders')
      .update({
        status: 'sent',
        sent_at: new Date().toISOString()
      })
      .eq('id', reminderId);
  }

  /**
   * Mark reminder as failed
   */
  private async markReminderAsFailed(reminderId: string, errorMessage: string): Promise<void> {
    await supabase
      .from('session_reminders')
      .update({
        status: 'failed',
        error_message: errorMessage,
        sent_at: new Date().toISOString()
      })
      .eq('id', reminderId);
  }

  /**
   * Get user reminder settings (or return defaults)
   */
  private async getUserReminderSettings(userId: string): Promise<ReminderSettings> {
    try {
      const { data, error } = await supabase
        .from('user_reminder_settings')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error || !data) {
        // Return default settings if no custom settings found
        return this.defaultSettings;
      }

      return {
        emailEnabled: data.email_enabled ?? true,
        smsEnabled: data.sms_enabled ?? false,
        pushEnabled: data.push_enabled ?? false,
        reminderTimes: data.reminder_times ?? this.defaultReminderTimes
      };

    } catch (error) {
      console.error('Error fetching user reminder settings:', error);
      return this.defaultSettings;
    }
  }

  /**
   * Update user reminder settings
   */
  async updateUserReminderSettings(userId: string, settings: Partial<ReminderSettings>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_reminder_settings')
        .upsert({
          user_id: userId,
          email_enabled: settings.emailEnabled,
          sms_enabled: settings.smsEnabled,
          push_enabled: settings.pushEnabled,
          reminder_times: settings.reminderTimes,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error updating reminder settings:', error);
        return false;
      }

      return true;

    } catch (error) {
      console.error('Error in updateUserReminderSettings:', error);
      return false;
    }
  }

  /**
   * Cancel reminders for a session (when session is cancelled/rescheduled)
   */
  async cancelSessionReminders(sessionId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('session_reminders')
        .update({
          status: 'cancelled',
          sent_at: new Date().toISOString()
        })
        .eq('session_id', sessionId)
        .eq('status', 'pending');

      if (error) {
        console.error('Error cancelling session reminders:', error);
        return false;
      }

      console.log(`✅ Cancelled reminders for session ${sessionId}`);
      return true;

    } catch (error) {
      console.error('Error in cancelSessionReminders:', error);
      return false;
    }
  }

  /**
   * Get reminder history for a session
   */
  async getSessionReminderHistory(sessionId: string): Promise<SessionReminderData[]> {
    try {
      const { data, error } = await supabase
        .from('session_reminders')
        .select('*')
        .eq('session_id', sessionId)
        .order('reminder_time', { ascending: true });

      if (error) {
        console.error('Error fetching reminder history:', error);
        return [];
      }

      return data || [];

    } catch (error) {
      console.error('Error in getSessionReminderHistory:', error);
      return [];
    }
  }

  /**
   * Manual trigger for sending immediate reminder
   */
  async sendImmediateReminder(sessionId: string): Promise<boolean> {
    try {
      console.log(`📧 Sending immediate reminder for session ${sessionId}`);

      const session = await this.getSessionForReminder(sessionId);
      if (!session) {
        console.error(`❌ Session ${sessionId} not found`);
        return false;
      }

      // Validate required data
      if (!session.student_profile || !session.tutor_profile || !session.topic) {
        console.error('Cannot send immediate reminder: missing required session data');
        return false;
      }

      // Calculate minutes until session
      const sessionTime = new Date(session.scheduled_at);
      const now = new Date();
      const minutesUntilSession = Math.round((sessionTime.getTime() - now.getTime()) / (1000 * 60));

      // Prepare reminder data
      const emailData = {
        studentName: `${session.student_profile.first_name} ${session.student_profile.last_name}`,
        tutorName: `${session.tutor_profile.first_name} ${session.tutor_profile.last_name}`,
        tutorEmail: session.tutor_profile.email,
        sessionDate: format(sessionTime, 'EEEE, MMMM d, yyyy'),
        sessionTime: format(sessionTime, 'h:mm a'),
        subject: session.topic.name,
        meetingUrl: session.meeting_session?.meeting_url || '',
        sessionType: session.session_type,
        minutesUntilSession: Math.max(minutesUntilSession, 0),
        timezone: session.student_profile.timezone || 'UTC'
      };

      // Send email reminder
      const emailResult = await emailService.sendSessionReminder(
        session.student_profile.email,
        emailData
      );

      if (emailResult.success) {
        console.log(`✅ Immediate reminder sent successfully for session ${sessionId}`);
        return true;
      } else {
        console.error(`❌ Failed to send immediate reminder for session ${sessionId}:`, emailResult.error);
        return false;
      }

    } catch (error) {
      console.error(`❌ Error sending immediate reminder:`, error);
      return false;
    }
  }
}

export const sessionReminderService = new SessionReminderService();